import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import { useContext } from 'react'
import { ToastContext } from '../../../context/toastContextCore'
import { useRoles } from '../context/RolesContext'

export const OwnerIdentityChip = ({
  onForceRefresh,
  refreshBusy = false,
}: {
  onForceRefresh?: () => Promise<void>
  refreshBusy?: boolean
}) => {
  const { t } = useNls()
  const { currentUser, signOutUser } = useAuth()
  const { role } = useRoles()
  const toast = useContext(ToastContext)
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const email = currentUser?.email || 'owner'
  const initials = (email.split('@')[0] || 'OW').slice(0, 2).toUpperCase()
  const uid = currentUser?.uid || ''

  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  const handleCopyUid = () => {
    if (!uid) return
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(uid)
      toast?.showToast(t('NLSOwnerConsoleUidCopied'), 'info')
    }
    setMenuOpen(false)
  }

  const handleForceRefreshClick = async () => {
    setMenuOpen(false)
    if (onForceRefresh) {
      await onForceRefresh()
    }
  }

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOutUser()
  }

  return (
    <div className="ownerIdentityContainer" ref={containerRef}>
      <button
        type="button"
        className="ownerIdentityChip"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
      >
        <div className="ownerIdentityAvatar" aria-hidden="true">{initials}</div>
        <span className="ownerIdentityEmail">{email}</span>
        <span className="ownerIdentityRole">{role}</span>
      </button>

      {menuOpen && (
        <div className="ownerIdentityMenu" role="menu">
          <button
            type="button"
            className="ownerIdentityMenuItem"
            role="menuitem"
            onClick={handleCopyUid}
          >
            {t('NLSOwnerConsoleCopyUid')}
          </button>
          {onForceRefresh && (
            <button
              type="button"
              className="ownerIdentityMenuItem"
              role="menuitem"
              disabled={refreshBusy}
              onClick={() => void handleForceRefreshClick()}
            >
              {refreshBusy ? t('NLSOwnerConsoleLoadingData') : t('NLSOwnerConsoleForceRefresh')}
            </button>
          )}
          <button
            type="button"
            className="ownerIdentityMenuItem ownerIdentityMenuItem--danger"
            role="menuitem"
            onClick={() => void handleSignOut()}
          >
            {t('NLSOwnerConsoleSignOut')}
          </button>
        </div>
      )}
    </div>
  )
}
